import React, {useContext} from 'react';
import PropTypes from 'prop-types';

import {TrackingContext} from '@homeaway/react-event-tracking';
import {Dropdown, DropdownItem} from '@homeaway/react-dropdown';
import {SVGIcon} from '@homeaway/react-svg';
import {CHEVRON_DOWN__16} from '@homeaway/svg-defs';

import travelerSvgMap from '../../utils/traveler-svgs';
import {namedEvents} from '../../utils/edap';

const splitStringSeparator = '&service=';
const minSplitStringsCount = 2;

function Traveler({avatar, items, label}) {
    // Add the context to be used for pushing edap events
    const context = useContext(TrackingContext);

    const edapTrigger = (item) => {
        const field = {
            actionlocation: `header.${item.text.replace(/\s/g, '').toLowerCase()}`
        };
        context.trigger('header.link.click', field);

        if (item.cssClass === 'se-traveler-trips') {
            context.trigger(namedEvents.TRIP_COLLECTION_SELECTED, {
                action_location: 'header'
            });
        }
    };

    function createClickHandler(item) {
        return (event) => {
            if (!item.link) {
                return false;
            }

            edapTrigger(item);

            // skip sub URL logic as link has already been manually updated
            if (item.linkUpdated) {
                return true;
            }

            const splitString = item.link.split(splitStringSeparator);

            // check split string contains splitStringSeparator
            if (splitString.length < minSplitStringsCount) {
                return true;
            }

            // CAS Logout URLs need to be single enocded because of how their params are structured:
            // https://{DOMAIN}/haod/auth/signout.html?service={SINGLE_ENCODED_RETURN_URL}
            const singleEncodedURI = encodeURIComponent(window.location.href);

            // if traveler logged in, perform specific link update
            const newLink = `${splitString[0]}${splitStringSeparator}${singleEncodedURI}`;

            // if we generate a new link, user has navigated to a sub URL in the same application
            // and we should redirect them there.  Otherwise let default user-agent behavior handle
            // rendered link
            if (newLink !== item.link) {
                event.stopPropagation();
                event.preventDefault();
                window.location.assign(newLink);
            }

            return true;
        };
    }

    function clickHandlerOnUserButton() {
        edapTrigger({text: 'user'});
    }

    const renderItem = (item, index) => {
        if (item.cssClass === 'divider') {
            return (
                <DropdownItem data-testid="traveler-item" className="divider" key={index} />
            );
        }

        return (
            <DropdownItem
                data-testid="traveler-item"
                dataBypass
                key={index}
                link={item.link}
                onClick={createClickHandler(item)}
                svgMarkup={travelerSvgMap[item.icon]}
                text={item.text}
            />
        );
    };

    if (!items) {
        return null;
    }

    let id = 'site-header__traveler';

    const toggle = (
        <button
            aria-expanded="false"
            aria-haspopup="true"
            className="site-header-nav__toggle Dropdown__toggle"
            id={id}
        >
            <img
                alt="avatar"
                aria-hidden="true"
                className="site-header-nav__avatar img-circle"
                src={avatar.image}
            />
            <span className="site-header-nav__icon-label">{label}</span>
            <SVGIcon className="Dropdown__toggle-icon" inlineFlex markup={CHEVRON_DOWN__16}/>
        </button>
    );

    return (
        <Dropdown
            className="site-header-nav__dropdown"
            customToggle={toggle}
            dropdownRight
            id={id}
            label={label}
            onDropdownShow={clickHandlerOnUserButton}
        >
            {items.map(renderItem)}
        </Dropdown>
    );
}

Traveler.propTypes = {
    avatar: PropTypes.shape({
        image: PropTypes.string
    }),
    items: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.string,
        link: PropTypes.string,
        text: PropTypes.string
    })),
    label: PropTypes.string
};

export {Traveler};
